import React from 'react';
import { Alert } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatCurrencyLocalized } from '../../../utils/currency';
import { formatLocalizedPercent } from '../../../utils/numberFormatting';
import { OrderMarginExplanationDto } from '../api/ordersApi';
import { hasRevenueForMarginPercent } from '../orderFinanceDisplay';
import './OrderMarginBuildUp.css';

interface OrderMarginBuildUpProps {
  marginExplanation: OrderMarginExplanationDto | null;
}

export const OrderMarginBuildUp: React.FC<OrderMarginBuildUpProps> = ({ marginExplanation }) => {
  const { t, i18n } = useTranslation(['orders']);
  const formatMoney = (value: number | string | null | undefined) =>
    formatCurrencyLocalized(value, 'PLN', i18n.language);

  if (!marginExplanation) {
    return null;
  }

  const { totals, orderLevelCosts } = marginExplanation;
  const supplierPurchaseCostPLN = totals.supplierPurchaseCostPLN || 0;
  const supplierLandedCostPLN = totals.supplierLandedCostPLN || 0;
  const supplierManualHistoricalCostPLN = totals.supplierManualHistoricalCostPLN || 0;
  const supplierAllocationRoundingAdjustmentPLN = totals.supplierAllocationRoundingAdjustmentPLN || 0;
  const supplierAllocatedRepresentedCostPLN = totals.supplierAllocatedRepresentedCostPLN || 0;

  // The business-facing goods cost is the net effect of supplier allocations and
  // warehouse issues. Individual ledger movements remain available below.
  const goodsCostPLN = supplierPurchaseCostPLN
    + supplierLandedCostPLN
    + supplierManualHistoricalCostPLN
    + supplierAllocationRoundingAdjustmentPLN
    - supplierAllocatedRepresentedCostPLN
    + (orderLevelCosts.cogsPLN || 0)
    + (orderLevelCosts.productFallbackCostPLN || 0);
  const transportCostPLN = orderLevelCosts.transportCostPLN || 0;
  const otherCostsPLN = totals.totalCostPLN - goodsCostPLN - transportCostPLN;

  const costRows = [
    {
      key: 'goods',
      label: t('orders:orderDetail.goodsCostCategory'),
      description: t('orders:orderDetail.goodsCostCategoryDescription'),
      value: goodsCostPLN,
    },
    {
      key: 'transport',
      label: t('orders:orderDetail.transportCostCategory'),
      description: t('orders:orderDetail.transportCostCategoryDescription'),
      value: transportCostPLN,
    },
    {
      key: 'other',
      label: t('orders:orderDetail.otherOrderCostsCategory'),
      description: t('orders:orderDetail.otherOrderCostsCategoryDescription'),
      value: otherCostsPLN,
    },
  ].filter((row) => Math.abs(Number(row.value || 0)) >= 0.005);

  const goodsLedgerRows = [
    { key: 'supplierPurchaseCost', label: t('orders:orderDetail.supplierPurchaseCostCategory'), value: supplierPurchaseCostPLN },
    { key: 'supplierLandedCost', label: t('orders:orderDetail.supplierLandedCostCategory'), value: supplierLandedCostPLN },
    { key: 'supplierManualHistoricalCost', label: t('orders:orderDetail.supplierManualHistoricalCostCategory'), value: supplierManualHistoricalCostPLN },
    { key: 'supplierAllocationRoundingAdjustment', label: t('orders:orderDetail.supplierAllocationRoundingAdjustmentCategory'), value: supplierAllocationRoundingAdjustmentPLN },
    { key: 'supplierRepresentedByCogs', label: t('orders:orderDetail.supplierRepresentedByCogsCategory'), value: -supplierAllocatedRepresentedCostPLN },
    { key: 'warehouseCost', label: t('orders:orderDetail.warehouseCostCategory'), value: orderLevelCosts.cogsPLN },
    { key: 'productFallbackCost', label: t('orders:orderDetail.productFallbackCostCategory'), value: orderLevelCosts.productFallbackCostPLN },
  ].filter((row) => Math.abs(Number(row.value || 0)) >= 0.005);

  const exceptionRows = [
    { key: 'unassignedAllocationCost', label: t('orders:orderDetail.unassignedAllocationCost'), value: totals.unassignedAllocationCostPLN },
    { key: 'unexplainedCost', label: t('orders:orderDetail.unexplainedCost'), value: orderLevelCosts.unexplainedCostPLN },
  ].filter((row) => Math.abs(Number(row.value || 0)) >= 0.005);

  return (
    <section className="order-result-calculation">
      {totals.supplierAllocationCoverageConflict && (
        <Alert
          type="warning"
          showIcon
          message={t('orders:orderDetail.supplierAllocationCoverageConflictTitle')}
          description={t('orders:orderDetail.supplierAllocationCoverageConflictDescription')}
          className="order-result-calculation__alert"
        />
      )}

      <header className="order-result-calculation__header">
        <h3>{t('orders:orderDetail.resultBuildUpTitle')}</h3>
        <p>{t('orders:orderDetail.resultBuildUpDescription')}</p>
      </header>

      <div className="order-result-calculation__ledger">
        <div className="order-result-calculation__row order-result-calculation__row--revenue">
          <span>{t('orders:orderDetail.marginRevenueSection')}</span>
          <strong>+ {formatMoney(totals.revenuePLN)}</strong>
        </div>

        {costRows.map((row) => (
          <div className="order-result-calculation__row" key={row.key}>
            <span>
              <b>{row.label}</b>
              <small>{row.description}</small>
            </span>
            <strong>- {formatMoney(Math.abs(Number(row.value || 0)))}</strong>
          </div>
        ))}

        <div className="order-result-calculation__row order-result-calculation__row--total-cost">
          <span>{t('orders:orderDetail.marginTotalCosts')}</span>
          <strong>- {formatMoney(Math.abs(Number(totals.totalCostPLN || 0)))}</strong>
        </div>

        <div className={`order-result-calculation__result${totals.marginPLN < 0 ? ' order-result-calculation__result--loss' : ''}`}>
          <span>{t('orders:orderDetail.orderResult')}</span>
          <strong>
            {formatMoney(totals.marginPLN)} ({hasRevenueForMarginPercent(totals.revenuePLN)
              ? formatLocalizedPercent(totals.marginPercent, i18n.language)
              : t('orders:orderDetail.marginPercentUnavailable')})
          </strong>
        </div>
      </div>

      {goodsLedgerRows.length > 1 && (
        <details className="order-result-calculation__details">
          <summary>{t('orders:orderDetail.technicalCostDetails')}</summary>
          <p>{t('orders:orderDetail.technicalCostDetailsDescription')}</p>
          <div>
            {goodsLedgerRows.map((row) => (
              <div key={row.key}>
                <span>{row.label}</span>
                <strong>
                  {Number(row.value || 0) >= 0 ? '- ' : '+ '}
                  {formatMoney(Math.abs(Number(row.value || 0)))}
                </strong>
              </div>
            ))}
          </div>
        </details>
      )}

      {exceptionRows.length > 0 && (
        <div className="order-result-calculation__exceptions">
          {exceptionRows.map((row) => (
            <span key={row.key}>
              <WarningOutlined />
              {row.label}: {formatMoney(row.value)}
            </span>
          ))}
        </div>
      )}
    </section>
  );
};
